import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { listPublishedHelpArticles, createMySupportTicket } from "@/lib/academy.functions";

export const Route = createFileRoute("/aluno/ajuda")({
  ssr: false,
  component: AjudaPage,
});

function AjudaPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["academy", "help"],
    queryFn: () => listPublishedHelpArticles(),
    staleTime: 60_000,
  });
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [sent, setSent] = useState(false);

  const submit = useMutation({
    mutationFn: () => createMySupportTicket({ data: { subject, message, whatsapp: whatsapp || undefined } }),
    onSuccess: () => {
      setSent(true);
      setSubject("");
      setMessage("");
      qc.invalidateQueries({ queryKey: ["academy", "tickets"] });
    },
  });

  const filtered = (data ?? []).filter((a: any) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return a.title.toLowerCase().includes(s) || (a.category ?? "").toLowerCase().includes(s);
  });

  const grouped: Record<string, any[]> = {};
  for (const a of filtered) {
    const cat = a.category || "Geral";
    (grouped[cat] ||= []).push(a);
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Central de ajuda</h1>
        <p className="mt-1 text-sm text-muted-foreground">Encontre respostas rápidas ou abra um chamado.</p>
      </div>

      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Buscar por título ou categoria…"
        className="w-full rounded-lg border border-ink/10 bg-background/40 p-3 text-sm"
      />

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Carregando…</p>
      ) : (
        Object.entries(grouped).map(([cat, arts]) => (
          <section key={cat}>
            <h2 className="text-lg font-semibold">{cat}</h2>
            <ul className="mt-2 divide-y divide-ink/12 rounded-2xl border border-ink/12 shadow-[var(--shadow-soft)]">
              {arts.map((a: any) => (
                <li key={a.id}>
                  <button
                    onClick={() => setOpenId(openId === a.id ? null : a.id)}
                    className="flex w-full items-center justify-between px-4 py-3 text-left text-sm hover:bg-ink/[0.045]"
                  >
                    <span>{a.title}</span>
                    <span className="text-xs text-muted-foreground">{openId === a.id ? "−" : "+"}</span>
                  </button>
                  {openId === a.id && (
                    <div className="whitespace-pre-wrap border-t border-ink/12 bg-ink/[0.045] px-4 py-3 text-sm text-muted-foreground">
                      {a.body_md}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </section>
        ))
      )}

      <section className="card-glass">
        <h2 className="text-xl font-semibold">Não encontrou o que precisava?</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Abra um chamado ou fale conosco direto no WhatsApp.
        </p>
        {sent ? (
          <p className="mt-4 rounded-lg border border-primary/40 bg-primary/10 p-3 text-sm">
            ✓ Chamado enviado! Nossa equipe entrará em contato em breve.
          </p>
        ) : (
          <div className="mt-4 space-y-3">
            <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Assunto" className="w-full rounded-lg border border-ink/10 bg-background/40 p-3 text-sm" />
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} placeholder="Descreva sua dúvida ou problema" className="w-full rounded-lg border border-ink/10 bg-background/40 p-3 text-sm" />
            <input value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} placeholder="Seu WhatsApp (opcional)" className="w-full rounded-lg border border-ink/10 bg-background/40 p-3 text-sm" />
            <div className="flex flex-wrap gap-2">
              <button onClick={() => submit.mutate()} disabled={submit.isPending || subject.length < 3 || message.length < 5} className="btn-primary text-sm disabled:opacity-60">
                {submit.isPending ? "Enviando…" : "Abrir chamado"}
              </button>
              <a href={`https://wa.me/555596852045?text=${encodeURIComponent("Olá! Preciso de ajuda com a Academia SVG.")}`} target="_blank" rel="noopener" className="btn-outline text-sm">
                Falar no WhatsApp
              </a>
            </div>
            {submit.error && <p className="text-xs text-destructive">{(submit.error as Error).message}</p>}
          </div>
        )}
      </section>
    </div>
  );
}